import { API_BASE_URL } from "./apiBase";

/**
 * Site-wide search across the three places gerdoo.cloud content lives:
 *
 * - landing pages: Pagefind index built from the prerendered HTML by
 *   `npm run build` (dist/pagefind, served at /pagefind/);
 * - docs: the Nextra site's own Pagefind index (/support/docs/_pagefind/),
 *   merged into the same search so both rank together;
 * - blog: rendered by the backend, so there is no static index. The public
 *   post list is small, fetched once and matched on the client.
 *
 * The Pagefind bundles only exist in a production build, so in `vite dev`
 * page/docs search reports itself unavailable and only the blog is searched.
 */

export type SearchCategory =
  | "products"
  | "servers"
  | "marketplace"
  | "guides"
  | "docs"
  | "blog"
  | "compare"
  | "company";

/** Display order and labels of result groups / filter chips. */
export const SEARCH_CATEGORIES: { key: SearchCategory; label: string }[] = [
  { key: "products", label: "محصولات" },
  { key: "servers", label: "سکوی ابری" },
  { key: "marketplace", label: "بازارچه" },
  { key: "guides", label: "راهنماها" },
  { key: "docs", label: "مستندات" },
  { key: "blog", label: "وبلاگ" },
  { key: "compare", label: "مقایسه" },
  { key: "company", label: "درباره گردو" },
];

export type SearchResult = {
  id: string;
  category: SearchCategory;
  title: string;
  url: string;
  /** Safe HTML: Pagefind escapes page text and only adds <mark>. */
  excerptHtml: string;
};

export type SearchResponse = {
  results: SearchResult[];
  /** False when the Pagefind bundle could not be loaded (e.g. in dev). */
  indexAvailable: boolean;
};

// Held in a variable so neither tsc nor Vite tries to resolve it at build
// time: the bundle is written by Pagefind after the Vite build finishes.
const PAGEFIND_BUNDLE = "/pagefind/pagefind.js";
const DOCS_BASE = "/support/docs/";
const PAGEFIND_RESULT_LIMIT = 30;
const BLOG_RESULT_LIMIT = 5;

type PagefindResultData = {
  url: string;
  excerpt: string;
  meta: { title?: string };
};

type Pagefind = {
  options: (opts: Record<string, unknown>) => Promise<void>;
  mergeIndex: (path: string, opts?: Record<string, unknown>) => Promise<void>;
  init: () => Promise<void>;
  search: (term: string) => Promise<{ results: { id: string; data: () => Promise<PagefindResultData> }[] }>;
};

let pagefindPromise: Promise<Pagefind | null> | null = null;

function loadPagefind(): Promise<Pagefind | null> {
  pagefindPromise ??= (async () => {
    try {
      const pagefind = (await import(/* @vite-ignore */ PAGEFIND_BUNDLE)) as Pagefind;
      await pagefind.options({ excerptLength: 24 });
      // A missing docs index would fail every search, not just the docs half,
      // so probe it before merging.
      const docsIndex = await fetch(`${DOCS_BASE}_pagefind/pagefind-entry.json`, { method: "HEAD" }).catch(() => null);
      if (docsIndex?.ok) {
        await pagefind.mergeIndex(`${DOCS_BASE}_pagefind/`, { baseUrl: DOCS_BASE });
      }
      await pagefind.init();
      return pagefind;
    } catch {
      return null;
    }
  })();
  return pagefindPromise;
}

type BlogPost = {
  slug: string;
  title: string;
  excerpt: string;
  meta_description: string;
  keywords: string;
  tags: string;
};

let blogPromise: Promise<BlogPost[]> | null = null;

function loadBlogPosts(): Promise<BlogPost[]> {
  // Plain fetch, not the axios client: this module also ships in the blog
  // embed, where axios would be dead weight.
  blogPromise ??= fetch(`${API_BASE_URL}/blog/posts`)
    .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
    .then((data: unknown) => (Array.isArray(data) ? (data as BlogPost[]) : []))
    .catch(() => {
      // Let a later search retry instead of caching the failure.
      blogPromise = null;
      return [];
    });
  return blogPromise;
}

/** Start fetching both sources ahead of the first keystroke. */
export function preloadSiteSearch() {
  void loadPagefind();
  void loadBlogPosts();
}

/** Folds Arabic letter forms and ZWNJ so «مي‌خواهم» matches «میخواهم». */
function normalize(text: string) {
  return text
    .toLowerCase()
    .replace(/ي/g, "ی")
    .replace(/ك/g, "ک")
    .replace(/\u200c|\u200f|[\u064b-\u065f]/g, "");
}

function escapeHtml(text: string) {
  return text.replace(/[&<>"']/g, (ch) => `&#${ch.charCodeAt(0)};`);
}

function highlight(text: string, terms: string[]) {
  let html = escapeHtml(text);
  for (const term of terms) {
    const escaped = escapeHtml(term).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    html = html.replace(new RegExp(escaped, "gi"), (m) => `<mark>${m}</mark>`);
  }
  return html;
}

function searchBlog(posts: BlogPost[], query: string): SearchResult[] {
  const terms = normalize(query).split(/\s+/).filter(Boolean);
  if (terms.length === 0) return [];
  return posts
    .map((post) => {
      const title = normalize(post.title);
      const tags = normalize(`${post.tags} ${post.keywords}`);
      const body = normalize(`${post.excerpt} ${post.meta_description}`);
      let score = 0;
      for (const term of terms) {
        const termScore = (title.includes(term) ? 3 : 0) + (tags.includes(term) ? 2 : 0) + (body.includes(term) ? 1 : 0);
        if (termScore === 0) return null; // every term must match somewhere
        score += termScore;
      }
      return { post, score };
    })
    .filter((hit): hit is { post: BlogPost; score: number } => hit !== null)
    .sort((a, b) => b.score - a.score)
    .slice(0, BLOG_RESULT_LIMIT)
    .map(({ post }) => ({
      id: `blog:${post.slug}`,
      category: "blog" as const,
      title: post.title,
      url: `/blog/${encodeURIComponent(post.slug)}`,
      excerptHtml: highlight(post.excerpt || post.meta_description, query.trim().split(/\s+/)),
    }));
}

function categorize(url: string): SearchCategory {
  const path = url.split("#")[0];
  if (path === "/support/docs" || path.startsWith(DOCS_BASE)) return "docs";
  if (path.startsWith("/products/cloud-server/") || path === "/datacenters") return "servers";
  if (path.startsWith("/products/") || ["/free-host", "/pricing", "/features"].includes(path)) return "products";
  if (path.startsWith("/marketplace")) return "marketplace";
  if (path.startsWith("/guides")) return "guides";
  if (path.startsWith("/compare/")) return "compare";
  return "company";
}

function cleanTitle(title: string | undefined, url: string) {
  // Page titles carry the brand suffix ("… | خدمات ابری | گردو کلاد").
  return (title || url).split(" | ")[0].trim();
}

async function searchPagefind(pagefind: Pagefind, query: string): Promise<SearchResult[]> {
  const { results } = await pagefind.search(query);
  const data = await Promise.all(results.slice(0, PAGEFIND_RESULT_LIMIT).map((r) => r.data()));
  return data.map((d, i) => {
    const url = d.url.replace(/\.html$/, "").replace(/\.html#/, "#").replace(/\/index$/, "") || "/";
    return {
      id: `pf:${results[i].id}`,
      category: categorize(url),
      title: cleanTitle(d.meta.title, url),
      url,
      excerptHtml: d.excerpt,
    };
  });
}

export async function searchSite(query: string): Promise<SearchResponse> {
  const [pagefind, posts] = await Promise.all([loadPagefind(), loadBlogPosts()]);
  const pageResults = pagefind ? await searchPagefind(pagefind, query).catch(() => []) : [];
  return {
    results: [...pageResults, ...searchBlog(posts, query)],
    indexAvailable: pagefind !== null,
  };
}

/** Docs and blog are separate apps; everything else is a landing SPA route. */
export function isLandingRoute(result: SearchResult) {
  return result.category !== "docs" && result.category !== "blog";
}
